import { useCallback, useEffect, useState, type SetStateAction } from "react";

function read<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  const raw = new URL(window.location.href).searchParams.get(key);
  if (raw === null) return fallback;
  if (typeof fallback === "string" || fallback === null) return raw.slice(0, 500) as T;
  if (typeof fallback === "number") return (Number.isInteger(Number(raw)) && Number(raw) >= 1 && Number(raw) <= 100_000 ? Number(raw) : fallback) as T;
  if (typeof fallback === "boolean") return (raw === "true") as T;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return fallback;
    return Object.fromEntries(Object.entries(fallback as object).map(([name, value]) => [name,
      typeof parsed[name] === typeof value && (typeof value !== "number" || (Number.isInteger(parsed[name]) && parsed[name] > 0 && parsed[name] <= 100_000))
        ? parsed[name] : value])) as T;
  } catch { return fallback; }
}
function write(url: URL, key: string, value: unknown) {
  if (value === "" || value === null) url.searchParams.delete(key);
  else url.searchParams.set(key, typeof value === "string" ? value : JSON.stringify(value));
}
export function adminNavigate(workspace: string, values: Record<string, unknown> = {}) {
  const url = new URL(window.location.href);
  write(url, "workspace", workspace);
  for (const [key, value] of Object.entries(values)) write(url, key, value);
  window.history.pushState(null, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
export function useAdminUrlState<T>(key: string, fallback: T, mode: "replace" | "push" = "replace") {
  const [value, update] = useState(() => read(key, fallback));
  useEffect(() => {
    const restore = () => update(read(key, fallback));
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, [key]);
  const setValue = useCallback((action: SetStateAction<T>) => {
    // Resolve against the current URL so consecutive updates do not overwrite other filters.
    const previous = read(key, fallback);
    const next = typeof action === "function" ? (action as (value: T) => T)(previous) : action;
    const url = new URL(window.location.href);
    write(url, key, next);
    if (mode === "push") window.history.pushState(null, "", url);
    else window.history.replaceState(null, "", url);
    update(next);
  }, [key, mode]);
  return [value, setValue] as const;
}
